import { http } from '@pendo/http';
import isEmpty from 'lodash/isEmpty';
import { hasReasoningSummaries } from '@/ask-pendo/utils/message';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { v4 as uuid } from 'uuid';

function askPendoChatRoute () {
    if (hasSegmentFlag('leoDeepAgents')) return 'leo';
    if (hasSegmentFlag('leoSupervisor')) return 'supervisor';

    return 'chat';
}

function buildUrlWithParams (baseUrl, options = {}) {
    const allowedParams = ['revision', 'claude_worker_version', 'version', 'force'];
    const params = new URLSearchParams();

    allowedParams.forEach((param) => {
        if (options[param] !== undefined && options[param] !== null) {
            params.set(param, options[param]);
        }
    });

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export function fetchUserConversations ({ limit } = {}) {
    const params = limit ? { limit } : {};

    return http.get('/api/s/_SID_/ask-pendo/conversations', { params });
}

export function fetchConversation (conversationId) {
    return http.get(`/api/s/_SID_/ask-pendo/conversations/${conversationId}`);
}

export function fetchLenses () {
    return http.get('/api/s/_SID_/lenses');
}

export function postLens (lens) {
    return http.post('/api/s/_SID_/lenses', lens);
}

export function putLens (lens) {
    return http.put(`/api/s/_SID_/lenses/${lens.id}`, lens);
}

export function deleteLens (id) {
    return http.delete(`/api/s/_SID_/lenses/${id}`);
}

export function fetchLeoMemories () {
    return http.get('/api/s/_SID_/ask-pendo/memories');
}

// A memory id is a file path and can contain slashes, so it is joined raw into
// the wildcard route rather than encoded as a single path segment.
export function putLeoMemory (id, content) {
    return http.put(`/api/s/_SID_/ask-pendo/memories/${id}`, { content });
}

export function deleteLeoMemory (id) {
    return http.delete(`/api/s/_SID_/ask-pendo/memories/${id}`);
}

export function postReportTrigger (
    conversationId,
    reportId,
    triggerContent,
    options = {},
    employeeConfig = {},
    userRole = '',
    customInstructions = '',
) {
    // Forward revision from the page URL so tagged Cloud Run revisions receive the trigger too.
    const route = hasSegmentFlag('leoDeepAgents') ? 'leo' : 'supervisor';
    const pageParams = new URLSearchParams(window.location.search);
    const urlOptions = { ...options, revision: options.revision ?? pageParams.get('revision') };
    const url = buildUrlWithParams(`/api/streaming/s/_SID_/ask-pendo/${route}`, urlOptions);

    const payload = {
        conversation: {
            conversationId,
            messages: [{
                type: 'report_trigger',
                content: triggerContent,
                metadata: { report_id: reportId },
            }],
        },
    };

    if (!isEmpty(employeeConfig)) {
        payload.employeeConfig = employeeConfig;
    }

    if (userRole) {
        payload.userRole = userRole;
    }

    if (customInstructions) {
        payload.customInstructions = customInstructions;
    }

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, payload, requestOptions).then((res) => res.data);
}

export function postAskPendoChat (conversation, employeeConfig, options = {}, userRole = '', customInstructions = '', suggestedPrompt = false, preferredAppId = 0, activeLens = null) {
    const url = buildUrlWithParams(`/api/streaming/s/_SID_/ask-pendo/${askPendoChatRoute()}`, options);

    const payload = {
        conversation,
    };

    if (!isEmpty(employeeConfig)) {
        payload.employeeConfig = employeeConfig;
    }

    if (userRole) {
        payload.userRole = userRole;
    }

    if (customInstructions) {
        payload.customInstructions = customInstructions;
    }

    if (suggestedPrompt) {
        payload.suggestedPrompt = true;
    }

    if (preferredAppId) {
        payload.preferredAppId = preferredAppId;
    }

    if (activeLens) {
        // Only the Lens's configured scope (audit fields stripped, null-y lists normalized
        // to []) is the contract the server-side entitlement resolution consumes.
        payload.lens = {
            id: activeLens.id,
            name: activeLens.name,
            apps: activeLens.apps || [],
            productAreas: activeLens.productAreas || [],
            segments: activeLens.segments || [],
            dateRange: activeLens.dateRange?.type ? activeLens.dateRange : null,
            customInstructions: activeLens.customInstructions || null,
        };
    }

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    // Pass through AbortController signal if provided
    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, payload, requestOptions).then((res) => res.data);
}

export function postAskPendoGuides (conversation, options = {}) {
    const url = buildUrlWithParams('/api/s/_SID_/ask-pendo/guides', options);

    return http.post(url, { conversation }).then((res) => res.data);
}

export function postAskPendoGuidesStream (conversation, options = {}) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/ask-pendo/guides', options);
    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http
        .post(url, { conversation }, requestOptions)
        .then((res) => res.data);
}

export function postAskPendoReaction ({ conversationId, reactionType, messageId, feedbackComment } = {}) {
    if (!conversationId || !reactionType || !messageId) {
        return Promise.reject(new Error('conversationId, reactionType, and messageId are required'));
    }
    const body = { conversationId, reactionType, messageId };
    if (feedbackComment) body.feedbackComment = feedbackComment;

    return http.post('/api/s/_SID_/ask-pendo/reaction', body).then((res) => res.data);
}

export function putAskPendoConversation (conversation = {}) {
    const { conversationId } = conversation;
    if (!conversationId) return Promise.reject(new Error('Conversation ID is required'));

    return http.put(`/api/s/_SID_/ask-pendo/conversations/${conversationId}`, conversation ).then((res) => res.data);
}

export function postAskPendoSignals (conversation, _employeeConfig = {}, options = {}, _userRole = '', customInstructions = '', _suggestedPrompt = false, preferredAppId = 0, activeLens = null) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/insight/generate', options);
    const payload = {
        conversation: {
            conversationId: conversation.conversationId,
            // Send only type + content so the request body is stable across calls — the per-message
            // timestamp addMessage stamps would otherwise bust the backend's response cache.
            messages: (conversation.messages || []).map(({ type, content }) => ({ type, content })),
        },
    };

    if (customInstructions) {
        payload.customInstructions = customInstructions;
    }

    if (preferredAppId) {
        payload.preferredAppId = preferredAppId;
    }

    if (activeLens) {
        payload.lens = {
            id: activeLens.id,
            name: activeLens.name,
            apps: activeLens.apps || [],
            productAreas: activeLens.productAreas || [],
            segments: activeLens.segments || [],
            dateRange: activeLens.dateRange?.type ? activeLens.dateRange : null,
            customInstructions: activeLens.customInstructions || null,
        };
    }

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, payload, requestOptions).then((res) => res.data);
}

export function refreshLiveArtifact ({ conversationId, artifactId } = {}, options = {}) {
    const url = buildUrlWithParams('/api/s/_SID_/ask-pendo/artifacts/refresh', options);

    return http.post(url, { conversationId, artifactId }).then((res) => res.data);
}

export function postAskPendoListen (conversation, _employeeConfig = {}, options = {}) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/ask-pendo/listen', options);

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    // Pass through AbortController signal if provided
    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http
        .post(
            url,
            {
                conversation,
            },
            requestOptions,
        )
        .then((res) => res.data);
}

export function postClaudeAgent (conversation, employeeConfig = {}, options = {}, userRole = '', customInstructions = '', suggestedPrompt = false, preferredAppId = 0) {
    const pageParams = new URLSearchParams(window.location.search);
    const claudeWorkerVersion = pageParams.get('claude_worker_version');
    const urlOptions = claudeWorkerVersion ? { ...options, claude_worker_version: claudeWorkerVersion } : options;
    const url = buildUrlWithParams('/api/streaming/s/_SID_/claude-agent/query', urlOptions);

    const payload = {
        conversation: {
            ...conversation,
            messages: conversation.messages?.filter((msg) => msg.type !== 'reasoning') || [],
        },
        config: {},
    };

    if (!isEmpty(employeeConfig)) {
        payload.config.employeeConfig = employeeConfig;
    }

    if (userRole) {
        payload.config.userRole = userRole;
    }

    if (customInstructions) {
        payload.config.customInstructions = customInstructions;
    }

    if (suggestedPrompt) {
        payload.suggestedPrompt = true;
    }

    if (preferredAppId) {
        payload.preferredAppId = preferredAppId;
    }

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, payload, requestOptions).then((res) => res.data);
}

export function postManagedAgentConversation () {
    const pageParams = new URLSearchParams(window.location.search);
    const options = {
        revision: pageParams.get('revision'),
        version: pageParams.get('version'),
        claude_worker_version: pageParams.get('claude_worker_version'),
    };
    const url = buildUrlWithParams('/api/streaming/s/_SID_/claude-managed-agent/conversation', options);

    return http.post(url)
        .then((res) => res.data);
}

export async function createConversationId (managedAgentEnabled) {
    if (managedAgentEnabled) {
        const { conversation_id: conversationId } = await postManagedAgentConversation();

        return conversationId;
    }

    return uuid();
}

export function postManagedClaudeAgent (conversation, employeeConfig = {}, options = {}, userRole = '', customInstructions = '', suggestedPrompt = false, preferredAppId = 0) {
    const pageParams = new URLSearchParams(window.location.search);
    const urlOptions = {
        ...options,
        claude_worker_version: pageParams.get('claude_worker_version'),
    };
    const url = buildUrlWithParams('/api/streaming/s/_SID_/claude-managed-agent/query', urlOptions);

    const payload = {
        conversation: {
            ...conversation,
            messages: conversation.messages?.filter((msg) => msg.type !== 'reasoning') || [],
        },
        config: {},
    };

    if (!isEmpty(employeeConfig)) {
        payload.config.employeeConfig = employeeConfig;
    }

    if (userRole) {
        payload.config.userRole = userRole;
    }

    if (customInstructions) {
        payload.config.customInstructions = customInstructions;
    }

    if (suggestedPrompt) {
        payload.suggestedPrompt = true;
    }

    if (preferredAppId) {
        payload.preferredAppId = preferredAppId;
    }

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, payload, requestOptions).then((res) => res.data);
}

export function fetchClaudeAgentFile (conversationId, filepath) {
    return http.get(`/api/s/_SID_/claude-agent/files/${conversationId}/${filepath}`);
}

export function fetchConversationFilePage (conversationId, fileId, { pageToken, pageSize = 50 } = {}) {
    const params = { page_size: pageSize };
    if (pageToken) {
        params.page_token = pageToken;
    }

    return http.get(`/api/s/_SID_/ask-pendo/conversations/${conversationId}/file/${fileId}`, { params })
        .then(({ data }) => ({
            rows: Array.isArray(data.rows) ? data.rows : [],
            nextPageToken: data.next_page_token ?? null,
            rowCount: data.row_count ?? null,
        }));
}

export function getAskPendoPostRequest (type) {
    switch (type) {
        case 'guides':
            return (conversation, _employeeConfig = {}, options) => postAskPendoGuides(conversation, options);
        case 'guides-stream':
            return (conversation, _employeeConfig = {}, options) => postAskPendoGuidesStream(conversation, options);
        case 'chat':
            return (conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId, activeLens) =>
                postAskPendoChat(conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId, activeLens);
        case 'listen':
            return (conversation, employeeConfig, options) => postAskPendoListen(conversation, employeeConfig, options);
        case 'agent':
            return (conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId) =>
                postClaudeAgent(conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId);
        case 'managed-agent':
            return (conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId) =>
                postManagedClaudeAgent(conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId);
        case 'signals':
            return (conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId, activeLens) =>
                postAskPendoSignals(conversation, employeeConfig, options, userRole, customInstructions, suggestedPrompt, preferredAppId, activeLens);
        default:
            return undefined;
    }
}

export function getInitialState () {
    return {
        userConversations: [],
        recentConversations: [],
        conversations: {},
        pendingResponses: {}, // Track which conversations are waiting for assistant response
        activeStreamIds: {}, // Track the currently active stream ID for each conversation
        abortControllers: {}, // Track AbortController instances per conversation
        employeeConfig: {},
        customInstructions: '', // Track custom instructions for the user
        preferredAppId: 0,
        lenses: [],
        lensesLoaded: false,
        memories: [],
        memoriesLoaded: false,
        activeLensId: null,
        lensRestoreState: 'pending', // 'pending' | 'done' | 'failed'
        configDrawerOpen: false, // Track whether the config drawer is open
        mentionShowOutOfLensScope: false,
        mentionScopeLensName: null,
        artifactConfig: {}, // Track local changes to artifact configurations for each artifact
        artifactMenuItems: {}, // Track dropdown menu items associated with each artifact
        // Per-conversation map of in-flight long-running Reports the FE is polling.
        // Shape: { [conversationId]: { [reportId]: { report_id, report_type, ...meta } } }
        // The supervisor emits `pending_report` SSE chunks and a terminal
        // `final_state.pending_reports` field; both feed into this map.
        pendingReports: {},
    };
}

const isBlank = (item) => !item || item.trim() === '';

export function parseMessageContent (message) {
    let content = '';
    let type = null;
    let reasoningSummaries = null;

    if (typeof message.content === 'string') {
        type = 'text';
        content = message.content;
    }

    if (Array.isArray(message.content)) {
        const textContent = message.content.find((el) => el.type === 'text');

        if (textContent) {
            type = 'text';
            content = textContent.text;
        }

        if (hasReasoningSummaries(message)) {
            const reasoningContent = message.content.find((el) => el.type === 'reasoning');
            reasoningSummaries = reasoningContent.summary;
        }
    }

    return { id: message.id, type, content, reasoningSummaries };
}

export const STOPPED_BY_USER_MESSAGE = 'Request was stopped by the user.';

/**
 * Leaving the page stops the answer as surely as pressing stop does, but it does not feel like the
 * same act, so say which one happened. Leo can now be read from a card the user is meant to carry
 * around the app, which makes this the more common of the two.
 */
export const STOPPED_BY_NAVIGATION_MESSAGE = 'Request was stopped because you left the page.';

const STOP_NOTICES = new Set([STOPPED_BY_USER_MESSAGE, STOPPED_BY_NAVIGATION_MESSAGE]);

/** Whether the content is one of Leo's own "this went no further" notices rather than an answer. */
export function isStopNotice (content) {
    return STOP_NOTICES.has(content);
}

export function cleanupIncompleteStoppedMessages (messages) {
    for (let i = 0; i < messages.length - 1; i++) {
        const a = messages[i];
        const b = messages[i + 1];
        const aStopped =
            a &&
            a.type === 'assistant' &&
            isStopNotice(parseMessageContent(a).content) &&
            !a.tool_calls;
        const bRealAssistant =
            b &&
            b.type === 'assistant' &&
            !isBlank(parseMessageContent(b).content) &&
            !isStopNotice(parseMessageContent(b).content) &&
            !b.tool_calls;

        if (aStopped && bRealAssistant) {
            messages.splice(i, 1);
            i--;
        }
    }
}

export function appendGuideNextActionArtifact (conversation, $t, { segment }) {
    const content = $t('askPendo.guide.Segment was created');
    const name = $t('askPendo.guide.Create guide');
    const artifactId = uuid();
    const nextActionGuideArtifact = {
        data: {
            segment_id: segment.id,
        },
        artifact_id: artifactId,
        id: uuid(),
        name,
        action_type: 'create_guide',
        type: 'next_actions',
    };

    const nextActionsGuideMessage = {
        content,
        id: uuid(),
        type: 'assistant',
        metadata: {
            artifact_ids: [artifactId],
        },
    };

    if (!conversation.artifacts) {
        conversation.artifacts = {};
    }

    conversation.artifacts[artifactId] = nextActionGuideArtifact;
    conversation.messages.push(nextActionsGuideMessage);
    conversation.last_artifact_id = artifactId;

    return conversation;
}
